import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import { buildGoogleMapLink, parseGoogleMapsLink, isShortMapLink } from '../../../utils/mapCoordinates';
import { ordersAPI } from '../../../config/orders-api';
import { getMapboxToken } from '../../../utils/utils';
import './LocationPickerModal.css';

const mapboxgl = window.mapboxgl;
const QATAR_CENTER = { lat: 25.2854, lng: 51.5310 };
const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };

const parseInitialCoords = (initial, fallback) => {
  const latN = parseFloat(initial?.lat);
  const lngN = parseFloat(initial?.lng);
  const valid =
    Number.isFinite(latN)
    && Number.isFinite(lngN)
    && Math.abs(latN) <= 90
    && Math.abs(lngN) <= 180
    && !(latN === 0 && lngN === 0);

  return {
    lat: valid ? Number(latN.toFixed(6)) : Number(fallback.lat),
    lng: valid ? Number(lngN.toFixed(6)) : Number(fallback.lng),
  };
};

/**
 * Mount this modal only while open (parent should use `{isOpen && <LocationPickerModal ... />}`)
 * so Mapbox and the pin always start fresh.
 */
const LocationPickerModal = ({ onClose, onConfirm, initial, isIndia }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const defaultCenter = isIndia ? INDIA_CENTER : QATAR_CENTER;
  const start = parseInitialCoords(initial, defaultCenter);

  const [mapLinkInput, setMapLinkInput] = useState('');
  const [resolvingLink, setResolvingLink] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [placeName, setPlaceName] = useState(initial?.placeName || '');
  const [coords, setCoords] = useState({
    lat: start.lat.toFixed(6),
    lng: start.lng.toFixed(6),
  });

  const token = process.env.REACT_APP_MAPBOX_TOKEN || getMapboxToken();

  const syncMarker = useCallback((lng, lat, fly = true) => {
    if (!mapRef.current) return;
    if (markerRef.current) {
      markerRef.current.setLngLat([lng, lat]);
    } else {
      const marker = new mapboxgl.Marker({ color: '#4caf50', draggable: true })
        .setLngLat([lng, lat])
        .addTo(mapRef.current);

      marker.on('dragend', () => {
        const { lng: nextLng, lat: nextLat } = marker.getLngLat();
        setCoords({
          lat: nextLat.toFixed(6),
          lng: nextLng.toFixed(6),
        });
        setPlaceName('');
      });

      markerRef.current = marker;
    }
    if (fly) {
      mapRef.current.flyTo({ center: [lng, lat], zoom: Math.max(mapRef.current.getZoom(), 15) });
    }
  }, []);

  useEffect(() => {
    if (!token || !mapContainerRef.current || !mapboxgl) return undefined;

    mapboxgl.accessToken = token;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [start.lng, start.lat],
      zoom: isIndia ? 5 : 14,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const onClick = (e) => {
      const { lng, lat } = e.lngLat;
      setCoords({ lat: lat.toFixed(6), lng: lng.toFixed(6) });
      setPlaceName('');
      syncMarker(lng, lat, false);
      map.easeTo({ center: [lng, lat] });
    };

    map.on('click', onClick);
    mapRef.current = map;

    const ready = () => {
      map.resize();
      syncMarker(start.lng, start.lat, false);
    };

    if (map.loaded()) {
      ready();
    } else {
      map.on('load', ready);
    }

    // Modal layout can settle a tick later — force resize so clicks hit the map
    const resizeTimers = [50, 150, 300].map((ms) =>
      setTimeout(() => {
        if (mapRef.current) mapRef.current.resize();
      }, ms)
    );

    return () => {
      resizeTimers.forEach(clearTimeout);
      map.off('click', onClick);
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      map.remove();
      mapRef.current = null;
    };
    // Intentionally init once on mount (parent remounts when reopening)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isIndia]);

  const runSearch = async (query) => {
    if (!query || query.length < 2 || !token) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const countryCode = isIndia ? 'in' : 'qa';
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${token}&country=${countryCode}&limit=6`;
      const res = await fetch(url);
      const data = await res.json();
      setSearchResults(
        (data.features || []).map((f) => ({
          id: f.id,
          name: f.place_name,
          lng: f.center[0],
          lat: f.center[1],
        }))
      );
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const applyPastedLink = async (rawLink) => {
    const link = String(rawLink || '').trim();
    setMapLinkInput(link);
    if (!link) return;

    const parsed = parseGoogleMapsLink(link);
    if (parsed) {
      setCoords({ lat: String(parsed.lat), lng: String(parsed.lng) });
      setPlaceName('');
      syncMarker(Number(parsed.lng), Number(parsed.lat), true);
      return;
    }

    const looksLikeMap = isShortMapLink(link) || /google\.[^/\s]+\/maps|maps\.google|goo\.gl/i.test(link);
    if (!looksLikeMap) return;

    setResolvingLink(true);
    try {
      const result = await ordersAPI.extractCoordinates(link);
      if (result.success && result.coordinates) {
        const lat = String(result.coordinates.lat);
        const lng = String(result.coordinates.lng);
        setCoords({ lat, lng });
        setPlaceName('');
        syncMarker(Number(lng), Number(lat), true);
      }
    } catch {
      // Leave the typed link so the user can try again or pick the pin.
    } finally {
      setResolvingLink(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runSearch(searchQuery.trim());
  };

  const selectResult = (hit) => {
    setPlaceName(hit.name);
    setCoords({ lat: hit.lat.toFixed(6), lng: hit.lng.toFixed(6) });
    setSearchResults([]);
    setSearchQuery(hit.name);
    syncMarker(hit.lng, hit.lat, true);
  };

  const handleConfirm = () => {
    const lat = String(coords.lat).trim();
    const lng = String(coords.lng).trim();
    if (!lat || !lng) return;
    onConfirm({
      lat,
      lng,
      placeName,
      mapLink: buildGoogleMapLink(lat, lng),
    });
    onClose();
  };

  if (!token) {
    return (
      <div className="location-picker-overlay">
        <div className="location-picker-box">
          <p>Mapbox token missing. Add REACT_APP_MAPBOX_TOKEN to your .env file.</p>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="location-picker-overlay" onClick={onClose}>
      <div className="location-picker-box" onClick={(e) => e.stopPropagation()}>
        <div className="location-picker-header">
          <h3><MapPin size={18} /> Pick delivery location</h3>
          <button type="button" className="location-picker-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form className="location-picker-search" onSubmit={handleSearchSubmit}>
          <Search size={16} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={isIndia ? 'Search place in India...' : 'Search place in Qatar...'}
          />
          <button type="submit" disabled={searching}>
            {searching ? '...' : 'Search'}
          </button>
        </form>

        <div className="location-picker-paste">
          <input
            id="location-picker-map-link"
            type="text"
            value={mapLinkInput}
            onChange={(e) => setMapLinkInput(e.target.value)}
            onPaste={(e) => {
              const text = e.clipboardData?.getData('text');
              if (text) {
                e.preventDefault();
                applyPastedLink(text);
              }
            }}
            onBlur={() => applyPastedLink(mapLinkInput)}
            placeholder={resolvingLink ? 'Resolving map link...' : 'Paste Google Maps link'}
          />
        </div>

        {searchResults.length > 0 && (
          <ul className="location-picker-results">
            {searchResults.map((hit) => (
              <li key={hit.id}>
                <button type="button" onClick={() => selectResult(hit)}>
                  {hit.name}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div ref={mapContainerRef} className="location-picker-map" />
        <p className="location-picker-hint">Click the map or drag the pin to set the delivery location.</p>

        <div className="location-picker-footer">
          <div className="location-picker-coords">
            {placeName && <span className="location-picker-place">{placeName}</span>}
            <span>{coords.lat}, {coords.lng}</span>
          </div>
          <div className="location-picker-actions">
            <button type="button" className="location-picker-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="location-picker-confirm" onClick={handleConfirm}>
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPickerModal;
